import React, { useMemo, useState } from 'react';
import { AlertTriangle, GitMerge, Layers, ListTree, Users } from 'lucide-react';
import { CrossUnitFindings, FkRelationship, OrphanFinding, MasterViolationFinding, CompoundRuleViolation, GlobalVendorCluster } from './types';

interface CrossUnitTabProps {
  findings: CrossUnitFindings;
  unitLabelById: Record<string, string>;
  onApplyFinding: (findingKind: string, finding: any) => void;
}

type SubTab = 'relationships' | 'orphans' | 'master' | 'rules' | 'vendors';

const severityClass = (s?: string) => {
  if (s === 'high') return 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]';
  if (s === 'medium') return 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]';
  return 'bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]';
};

export const CrossUnitTab: React.FC<CrossUnitTabProps> = ({ findings, unitLabelById, onApplyFinding }) => {
  const [sub, setSub] = useState<SubTab>(() => {
    if (findings.orphans.length > 0) return 'orphans';
    if (findings.masterViolations.length > 0) return 'master';
    if (findings.compoundRuleViolations.length > 0) return 'rules';
    if (findings.globalVendorMaster.proposal_count > 0) return 'vendors';
    return 'relationships';
  });

  const labelOf = (uid: string) => unitLabelById[uid] || uid;

  const subTabs: { id: SubTab; label: string; count: number; icon: any }[] = [
    { id: 'relationships', label: 'Relationships', count: findings.fkRelationships.length, icon: GitMerge },
    { id: 'orphans', label: 'Orphan rows', count: findings.orphans.length, icon: ListTree },
    { id: 'master', label: 'Master violations', count: findings.masterViolations.length, icon: AlertTriangle },
    { id: 'rules', label: 'Compound rules', count: findings.compoundRuleViolations.length, icon: Layers },
    { id: 'vendors', label: 'Global vendor master', count: findings.globalVendorMaster.proposal_count, icon: Users },
  ];

  return (
    <div className="space-y-4">
      {findings.degraded && (
        <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-lg px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[#92400E] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[13px] font-semibold text-[#92400E]">Cross-sheet reconciliation ran in degraded mode</p>
              {findings.error && <p className="text-[12px] text-[#92400E] mt-0.5">{findings.error}</p>}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-1 border-b border-[#E2E8F0]">
        {subTabs.map(st => {
          const Icon = st.icon;
          return (
            <button
              key={st.id}
              onClick={() => setSub(st.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium border-b-2 transition-colors ${
                sub === st.id
                  ? 'text-[#7C3AED] border-[#7C3AED]'
                  : 'text-[#64748B] border-transparent hover:text-[#0F172A] hover:bg-[#F1F5F9]'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {st.label}
              {st.count > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold bg-[#FEF3C7] text-[#92400E]">{st.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {sub === 'relationships' && <RelationshipsTable items={findings.fkRelationships} labelOf={labelOf} />}
      {sub === 'orphans' && <OrphansTable items={findings.orphans} labelOf={labelOf} onApply={(f) => onApplyFinding('orphan', f)} />}
      {sub === 'master' && <MasterTable items={findings.masterViolations} labelOf={labelOf} onApply={(f) => onApplyFinding('master_violation', f)} />}
      {sub === 'rules' && <RulesTable items={findings.compoundRuleViolations} labelOf={labelOf} onApply={(f) => onApplyFinding('compound_rule', f)} />}
      {sub === 'vendors' && <VendorsPanel clusters={findings.globalVendorMaster.canonical_vendors} labelOf={labelOf} onApply={(f) => onApplyFinding('global_vendor_cluster', f)} />}
    </div>
  );
};

const RelationshipsTable: React.FC<{ items: FkRelationship[]; labelOf: (u: string) => string }> = ({ items, labelOf }) => {
  if (items.length === 0) return <EmptyState message="No cross-sheet relationships were inferred." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px] border-collapse">
        <thead>
          <tr className="text-left text-[12px] font-semibold uppercase tracking-wide text-[#64748B] border-b border-[#E2E8F0]">
            <th className="px-3 py-2">Child</th>
            <th className="px-3 py-2">Parent / Master</th>
            <th className="px-3 py-2">Kind</th>
            <th className="px-3 py-2 text-right">Overlap</th>
            <th className="px-3 py-2">Source</th>
          </tr>
        </thead>
        <tbody>
          {items.map(r => (
            <tr key={r.relationship_id} className="border-b border-[#F1F5F9] hover:bg-[#FAFCFF]">
              <td className="px-3 py-2"><span className="font-medium text-[#0F172A]">{labelOf(r.from_unit_id)}</span><span className="text-[#94A3B8]">.{r.from_column}</span></td>
              <td className="px-3 py-2"><span className="font-medium text-[#0F172A]">{labelOf(r.to_unit_id)}</span><span className="text-[#94A3B8]">.{r.to_column}</span></td>
              <td className="px-3 py-2">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  r.kind === 'foreign_key' ? 'bg-[#DBEAFE] text-[#1E40AF]' : 'bg-[#FEF3C7] text-[#92400E]'
                }`}>{r.kind === 'foreign_key' ? 'foreign key' : 'master lookup'}</span>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{r.overlap_pct.toFixed(1)}%</td>
              <td className="px-3 py-2 text-[12px] text-[#64748B]">{r.llm_confirmed ? 'AI confirmed' : 'heuristic'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OrphansTable: React.FC<{ items: OrphanFinding[]; labelOf: (u: string) => string; onApply: (f: OrphanFinding) => void }> = ({ items, labelOf, onApply }) => {
  if (items.length === 0) return <EmptyState message="No orphan rows detected across sheets." />;
  return (
    <div className="space-y-2">
      {items.map(o => (
        <div key={o.finding_id} className="border border-[#E2E8F0] rounded-lg p-3 bg-white">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${severityClass(o.severity)}`}>{o.severity.toUpperCase()}</span>
                <span className="text-[12px] text-[#64748B]">{o.orphan_count.toLocaleString()} orphan rows ({(o.orphan_pct ?? 0).toFixed(1)}% of non-empty)</span>
              </div>
              <p className="text-[13px] text-[#0F172A]">
                <span className="font-medium">{labelOf(o.from_unit_id)}.{o.from_column}</span>
                <span className="text-[#94A3B8]"> → </span>
                <span className="font-medium">{labelOf(o.to_unit_id)}.{o.to_column}</span>
              </p>
              {o.sample_orphan_values.length > 0 && (
                <p className="text-[12px] text-[#64748B] mt-1">
                  Sample invalid values: <span className="font-mono text-[#0F172A]">{o.sample_orphan_values.slice(0, 6).join(', ')}{o.sample_orphan_values.length > 6 ? '…' : ''}</span>
                </p>
              )}
            </div>
            <button
              onClick={() => onApply(o)}
              className="px-3 py-1.5 text-[12px] font-medium bg-[#7C3AED] text-white rounded-md hover:bg-[#6D28D9] transition-colors flex-shrink-0"
            >Resolve…</button>
          </div>
        </div>
      ))}
    </div>
  );
};

const MasterTable: React.FC<{ items: MasterViolationFinding[]; labelOf: (u: string) => string; onApply: (f: MasterViolationFinding) => void }> = ({ items, labelOf, onApply }) => {
  if (items.length === 0) return <EmptyState message="No master-list violations detected." />;
  return (
    <div className="space-y-2">
      {items.map(m => (
        <div key={m.finding_id} className="border border-[#E2E8F0] rounded-lg p-3 bg-white">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${severityClass(m.severity)}`}>{m.severity.toUpperCase()}</span>
                <span className="text-[12px] text-[#64748B]">{m.violation_count.toLocaleString()} invalid values ({(m.violation_pct ?? 0).toFixed(1)}%)</span>
              </div>
              <p className="text-[13px] text-[#0F172A]">
                <span className="font-medium">{labelOf(m.source_unit_id)}.{m.source_column}</span>
                <span className="text-[#94A3B8]"> not in </span>
                <span className="font-medium">{labelOf(m.master_unit_id)}.{m.master_column}</span>
              </p>
              {m.sample_invalid_values.length > 0 && (
                <p className="text-[12px] text-[#64748B] mt-1">
                  Sample invalid: <span className="font-mono text-[#0F172A]">{m.sample_invalid_values.slice(0, 8).join(', ')}{m.sample_invalid_values.length > 8 ? '…' : ''}</span>
                </p>
              )}
            </div>
            <button
              onClick={() => onApply(m)}
              className="px-3 py-1.5 text-[12px] font-medium bg-[#7C3AED] text-white rounded-md hover:bg-[#6D28D9] transition-colors flex-shrink-0"
            >Resolve…</button>
          </div>
        </div>
      ))}
    </div>
  );
};

const RulesTable: React.FC<{ items: CompoundRuleViolation[]; labelOf: (u: string) => string; onApply: (f: CompoundRuleViolation) => void }> = ({ items, labelOf, onApply }) => {
  if (items.length === 0) return <EmptyState message="No compound-rule violations detected." />;
  return (
    <div className="space-y-2">
      {items.map(r => (
        <div key={r.finding_id} className="border border-[#E2E8F0] rounded-lg p-3 bg-white">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${severityClass(r.severity)}`}>{r.severity.toUpperCase()}</span>
                <span className="text-[12px] text-[#64748B]">{r.violation_count.toLocaleString()} matching rows ({(r.violation_pct ?? 0).toFixed(1)}%)</span>
              </div>
              <p className="text-[13px] text-[#0F172A] font-medium">{r.rule_name}</p>
              <p className="text-[12px] text-[#64748B] mt-0.5">In <span className="font-medium">{labelOf(r.unit_id)}</span></p>
              <pre className="text-[11px] font-mono bg-[#F8FAFC] border border-[#E2E8F0] rounded px-2 py-1 mt-1 overflow-x-auto">{r.predicate_dsl}</pre>
            </div>
            <button
              onClick={() => onApply(r)}
              className="px-3 py-1.5 text-[12px] font-medium bg-[#7C3AED] text-white rounded-md hover:bg-[#6D28D9] transition-colors flex-shrink-0"
            >Resolve…</button>
          </div>
        </div>
      ))}
    </div>
  );
};

const VendorsPanel: React.FC<{ clusters: GlobalVendorCluster[]; labelOf: (u: string) => string; onApply: (c: GlobalVendorCluster) => void }> = ({ clusters, labelOf, onApply }) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  if (clusters.length === 0) return <EmptyState message="No global vendor clusters proposed." />;
  return (
    <div className="space-y-2">
      {clusters.map(c => {
        const isOpen = expanded === c.cluster_id;
        return (
          <div key={c.cluster_id} className="border border-[#E2E8F0] rounded-lg bg-white overflow-hidden">
            <div className="flex items-start justify-between gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#E0E7FF] text-[#3730A3]">{c.cluster_size} variants</span>
                  <span className="text-[12px] text-[#64748B]">across {c.member_units.length} sheet{c.member_units.length === 1 ? '' : 's'}</span>
                </div>
                <p className="text-[13px] font-semibold text-[#0F172A]">Canonical: {c.canonical_name}</p>
                <button
                  onClick={() => setExpanded(isOpen ? null : c.cluster_id)}
                  className="text-[12px] text-[#7C3AED] hover:underline mt-1"
                >{isOpen ? 'Hide' : 'Show'} variants</button>
              </div>
              <button
                onClick={() => onApply(c)}
                className="px-3 py-1.5 text-[12px] font-medium bg-[#7C3AED] text-white rounded-md hover:bg-[#6D28D9] transition-colors flex-shrink-0"
              >Apply…</button>
            </div>
            {isOpen && (
              <div className="border-t border-[#E2E8F0] bg-[#FAFCFF] px-3 py-2">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-[#64748B] text-left">
                      <th className="py-1">Sheet</th>
                      <th className="py-1">Column</th>
                      <th className="py-1">Variant</th>
                      <th className="py-1 text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.variants.map((v, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0]">
                        <td className="py-1">{labelOf(v.unit_id)}</td>
                        <td className="py-1 font-mono text-[#475569]">{v.column}</td>
                        <td className="py-1 font-mono text-[#0F172A]">{v.value}</td>
                        <td className="py-1 text-right tabular-nums">{v.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-center py-8 text-[13px] text-[#64748B]">{message}</div>
);

export default CrossUnitTab;
